// src/features/executive/performance/widgets/PerBranchRevenueCard.tsx
// Per-branch revenue breakdown. Autonomous widget: 1 hook + 4 states.
// Renders only when filter.branchId is null ("all branches"); when a single
// branch is picked, comparison is irrelevant and the widget hides itself.

import {
  Box, LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { usePerBranchRevenue } from '../hooks/use-per-branch-revenue'
import type { ExecutiveState } from 'app/slice/executive.slice'
import {
  achievementColor,
  formatCurrency,
  formatVariance,
} from 'utils/executive.utils'

const colorToToken = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  default: 'primary',
} as const

function signedCurrency(v: number | null): string {
  if (v == null) return '—'
  const sign = v > 0 ? '+' : v < 0 ? '-' : ''
  return `${sign}${formatCurrency(Math.abs(v))}`
}

// Column header with an info icon — hover reveals the column's definition.
// Mirrors the ℹ️ + Tooltip pattern used across the executive dashboard.
function HeaderCell({
  label,
  tip,
  align,
}: {
  label: string
  tip: string
  align?: 'left' | 'right'
}) {
  return (
    <TableCell align={align}>
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'middle' }}
      >
        {label}
        <Tooltip
          title={tip}
          arrow
          placement="top"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      </Box>
    </TableCell>
  )
}

export default function PerBranchRevenueCard() {
  const { t } = useTranslation()
  const branchId = useSelector(
    (s: { executive: ExecutiveState }) => s.executive.filters.branchId
  )
  const { data, loading, error, refetch } = usePerBranchRevenue()

  // Per-branch comparison is meaningless when the user has narrowed the
  // filter to exactly one branch. Hide the card in that case; show it for
  // "all" (branchId=null) AND any multi-select with 2+ branches.
  const selectedCount =
    branchId == null ? 0 : typeof branchId === 'number' ? 1 : branchId.split(',').filter(Boolean).length
  if (selectedCount === 1) return null

  const isEmpty = !data || data.rows.length === 0

  return (
    <WidgetCard
      title={t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.TITLE', 'รายได้แยกสาขา')}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={240}
    >
      {data && (
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeaderCell
                label={t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.BRANCH', 'สาขา')}
                tip={t(
                  'EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.BRANCH_TOOLTIP',
                  'ทุกสาขาที่เปิดใช้งาน เรียงตามรายได้สุทธิจากมากไปน้อย\nสาขาที่ยังไม่มีรายได้ในช่วงนี้จะแสดงเป็น ฿0'
                )}
              />
              <HeaderCell
                align="right"
                label={t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.ACTUAL', 'รายได้สุทธิ')}
                tip={t(
                  'EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.ACTUAL_TOOLTIP',
                  'รายได้สุทธิตามใบเสร็จ — เงื่อนไขการคิดเดียวกับ "รายได้รวม (สุทธิ)"\n• ใบเสร็จปกติ (หัก GV + โปรโมชั่นแล้ว)\n• รวมยอดเก็บค้างชำระ BL\n• ไม่รวมเงินรับล่วงหน้า (AD) — รับรู้เป็นรายได้เมื่อลูกค้าใช้บริการจริง ไม่ใช่ตอนฝากเงิน'
                )}
              />
              <HeaderCell
                align="right"
                label={t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.TARGET', 'เป้าหมาย')}
                tip={t(
                  'EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.TARGET_TOOLTIP',
                  'เป้ารายได้สำหรับช่วงเวลาที่เลือก ตั้งค่าที่หน้า "ตั้งค่าเป้าหมาย"\nรวมเป้ารายเดือนตามสัดส่วนจำนวนวันที่อยู่ในช่วง — เช่น ครึ่งเดือน = ครึ่งหนึ่งของเป้าทั้งเดือน'
                )}
              />
              <HeaderCell
                align="right"
                label={t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.PCT', '% ทำได้')}
                tip={t(
                  'EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.PCT_TOOLTIP',
                  'รายได้สุทธิ ÷ เป้าหมาย × 100\nแถบสี: เขียว ≥ 100% · เหลือง ≥ 70% · แดง < 70%'
                )}
              />
              <HeaderCell
                align="right"
                label={t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.VARIANCE', 'เทียบเป้า')}
                tip={t(
                  'EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.VARIANCE_TOOLTIP',
                  'ผลต่างระหว่างรายได้สุทธิกับเป้าหมาย เป็นจำนวนเงิน (บาท) และคิดเป็น % ของเป้า\nค่าบวก = เกินเป้า · ค่าลบ = ต่ำกว่าเป้า'
                )}
              />
              <HeaderCell
                align="right"
                label={t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.VS_PREV', 'vs งวดก่อน')}
                tip={t(
                  'EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.VS_PREV_TOOLTIP',
                  'เทียบรายได้สุทธิกับงวดก่อนหน้าที่มีจำนวนวันเท่ากัน — เช่น เดือนนี้เทียบเดือนก่อน\nค่าบวก = เติบโตขึ้น · ค่าลบ = ลดลง'
                )}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row) => {
              const token = colorToToken[achievementColor(row.pctAchieved)]
              return (
                <TableRow key={row.branchId}>
                  <TableCell>{row.branchName}</TableCell>
                  <TableCell align="right">{formatCurrency(row.actual)}</TableCell>
                  <TableCell align="right">
                    {row.target != null ? formatCurrency(row.target) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {row.pctAchieved != null ? `${row.pctAchieved.toFixed(1)}%` : '—'}
                    {row.pctAchieved != null && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, row.pctAchieved)}
                        color={token}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        row.targetVariance != null && row.targetVariance >= 0
                          ? 'success.main'
                          : 'error.main',
                    }}
                  >
                    {signedCurrency(row.targetVariance)}
                    {row.targetVariancePct != null && (
                      <Typography
                        variant="caption"
                        component="div"
                        color="inherit"
                        sx={{ display: 'block' }}
                      >
                        ({formatVariance(row.targetVariancePct)})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        row.periodVariance != null && row.periodVariance >= 0
                          ? 'success.main'
                          : 'error.main',
                    }}
                  >
                    {formatVariance(row.periodVariance)}
                  </TableCell>
                </TableRow>
              )
            })}
            {/* Totals row */}
            <TableRow sx={{ '& td': { fontWeight: 600, borderTop: 1, borderColor: 'divider' } }}>
              <TableCell>{t('EXECUTIVE.PERFORMANCE.PER_BRANCH_REVENUE.TOTAL', 'รวมทุกสาขา')}</TableCell>
              <TableCell align="right">{formatCurrency(data.totalActual)}</TableCell>
              <TableCell align="right">
                {data.totalTarget != null ? formatCurrency(data.totalTarget) : '—'}
              </TableCell>
              <TableCell align="right">
                {data.totalTarget != null && data.totalTarget > 0
                  ? `${((data.totalActual / data.totalTarget) * 100).toFixed(1)}%`
                  : '—'}
              </TableCell>
              <TableCell align="right" />
              <TableCell align="right" />
            </TableRow>
          </TableBody>
        </Table>
        </TableContainer>
      )}
    </WidgetCard>
  )
}
